import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5radar from '@amcharts/amcharts5/radar';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ReputationScoreWithTier } from '../../../../core/installer-profile';
import { Installer } from '../../../../domains/users';
import { KudosReason } from '../../../../domains/users/kudos-reason/kudos-reason.model';
import { createAm5Root } from '../../../../shared/charts';

/** The seven ratings a company gives after a completed project (1-10 each). */
const METRICS: { field: keyof KudosReason; label: string }[] = [
  { field: 'professionalismAverage', label: 'Professionalism' },
  { field: 'dependabilityAverage', label: 'Dependability' },
  { field: 'punctualityAverage', label: 'Punctuality' },
  { field: 'qualityAverage', label: 'Quality' },
  { field: 'attitudeAverage', label: 'Attitude' },
  { field: 'timelyCommunicationAverage', label: 'Timely Communication' },
  { field: 'likelyToUseAgainAverage', label: 'Likely To Hire Again' },
];

/** A radar needs at least three axes to read as a shape. */
const MIN_RADAR_AXES = 3;

/** Ratings are 1-10: only above the midpoint counts as "most positive feedback". */
const POSITIVE_ABOVE = 5;

export interface RatedMetric {
  label: string;
  value: number;
}

/**
 * TT-260. The Reputation Score card, back on the installer profile because
 * companies use it to choose installers. TT-234 removed it together with the
 * old sidebar bar, which read a column nothing has written since 2024; this
 * card reads the computed score (GET /users/:id/reputation-score) and the
 * averages of the seven ratings companies give after a completed project
 * (GET /users/:id/kudos-reasons).
 *
 * With no reviews it says so instead of showing a score, and it never plots a
 * rating that wasn't given.
 */
@Component({
  selector: 'gc-installer-reputation-score',
  templateUrl: './installer-reputation-score.component.html',
  styleUrls: ['./installer-reputation-score.component.scss'],
})
export class InstallerReputationScoreComponent implements OnDestroy, AfterViewInit, AfterViewChecked, OnChanges {
  @Input() installer!: Installer;
  @Input() reputationScore?: ReputationScoreWithTier;
  @Input() kudosReason?: KudosReason;

  readonly chartId = 'kudosReasonsRadarChart1';

  private kudosReasonsRoot?: am5.Root;
  private chartInitialized = false;

  constructor(
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  get completedProjectReviews(): number {
    const reviews = Number(this.kudosReason?.completedProjectReviews ?? 0);
    return Number.isFinite(reviews) ? reviews : 0;
  }

  /** 0-100, or null when there is nothing to base a score on. */
  get score(): number | null {
    const raw = Number(this.reputationScore?.reputationScore);
    if (!Number.isFinite(raw) || raw <= 0) {
      return null;
    }
    return Math.min(Math.max(Math.floor(raw), 0), 100);
  }

  get hasScore(): boolean {
    return this.score !== null;
  }

  /** Only ratings that were actually given; the API may send null for one nobody rated. */
  get ratedMetrics(): RatedMetric[] {
    const kudos = this.kudosReason;
    if (!kudos || this.completedProjectReviews <= 0) {
      return [];
    }
    return METRICS.filter(({ field }) => kudos[field] !== null && kudos[field] !== undefined)
      .map(({ field, label }) => ({ label, value: Number(kudos[field]) }))
      .filter(metric => Number.isFinite(metric.value) && metric.value > 0);
  }

  get showRadar(): boolean {
    return this.ratedMetrics.length >= MIN_RADAR_AXES;
  }

  /** "Professionalism and quality": the two highest ratings above the midpoint, or '' when none. */
  get strongestMetrics(): string {
    const top = [...this.ratedMetrics]
      .filter(metric => metric.value > POSITIVE_ABOVE)
      .sort((a, b) => b.value - a.value)
      .slice(0, 2)
      .map(metric => metric.label.toLowerCase());
    return top.join(' and ');
  }

  get firstName(): string {
    return this.installer?.firstName?.trim() || 'This installer';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reputationScore'] || changes['kudosReason']) {
      this.chartInitialized = false;
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    this.updateChart();
  }

  ngAfterViewChecked() {
    if (!this.chartInitialized && this.shouldRenderChart()) {
      this.updateChart();
    }
  }

  private shouldRenderChart(): boolean {
    return this.hasScore && this.showRadar && document.getElementById(this.chartId) !== null;
  }

  private updateChart() {
    if (!this.shouldRenderChart()) {
      return;
    }
    Promise.resolve().then(() => {
      this.zone.runOutsideAngular(() => {
        this.kudosReasonsRadarChart();
        this.changeDetectorRef.detectChanges();
      });
    });
  }

  private kudosReasonsRadarChart() {
    if (!document.getElementById(this.chartId)) {
      return;
    }
    this.kudosReasonsRoot?.dispose();
    this.kudosReasonsRoot = createAm5Root(this.chartId);
    const root = this.kudosReasonsRoot;

    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, { panX: false, panY: false, wheelX: 'none', wheelY: 'none' }),
    );
    const cursor = chart.set('cursor', am5radar.RadarCursor.new(root, { behavior: 'none' }));
    cursor.lineY.set('forceHidden', true);

    const xRenderer = am5radar.AxisRendererCircular.new(root, { strokeOpacity: 0.1, minGridDistance: 10 });
    xRenderer.labels.template.setAll({ forceHidden: true });
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'category', renderer: xRenderer }));

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 10,
        renderer: am5radar.AxisRendererRadial.new(root, { radius: am5.percent(90) }),
      }),
    );
    yAxis.get('renderer').labels.template.setAll({ forceHidden: true });

    const series = chart.series.push(
      am5radar.RadarLineSeries.new(root, {
        name: 'Ratings',
        xAxis,
        yAxis,
        valueYField: 'value',
        categoryXField: 'category',
        stroke: am5.color('#FF8C00'),
        fill: am5.color('#FF8C00'),
        opacity: 0.5,
        tooltip: am5.Tooltip.new(root, { labelText: '{categoryX}: {valueY}' }),
      }),
    );
    series.strokes.template.setAll({ strokeWidth: 2 });
    series.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: series.get('fill'),
          shadowColor: am5.color('#000000'),
          shadowBlur: 8,
          shadowOpacity: 0.5,
        }),
      }),
    );

    const data = this.ratedMetrics.map(metric => ({ category: metric.label, value: metric.value }));
    xAxis.data.setAll(data);
    series.data.setAll(data);
    series.appear(1200);
    chart.appear(1200, 250);
    this.chartInitialized = true;
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      this.kudosReasonsRoot?.dispose();
    });
  }
}
